import axios from 'axios';

// API configuration - simplified for production reliability
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://english-guru-backend.onrender.com';
const API_KEY = process.env.REACT_APP_API_KEY || 'development-api-key';

// Create axios instance with consistent configuration
const apiClient = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    timeout: 15000, // Increased timeout for production
    headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-API-Key': API_KEY, // Always include API key
    },
});

// Request interceptor
apiClient.interceptors.request.use(
    (config) => {
        // Ensure API key is always included
        if (!config.headers['X-API-Key']) {
            config.headers['X-API-Key'] = API_KEY;
        }

        // Debug logging
        console.log('API Request:', {
            url: config.url,
            method: config.method,
            baseURL: config.baseURL,
            hasApiKey: !!config.headers['X-API-Key'],
        });

        return config;
    },
    (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        console.log('API Response Success:', {
            status: response.status,
            url: response.config.url,
        });
        
        return response;
    },
    (error) => {
        console.error('API Response Error:', {
            message: error.message,
            status: error.response?.status,
            url: error.config?.url,
            response: error.response?.data,
            config: {
                baseURL: error.config?.baseURL,
                hasApiKey: !!error.config?.headers?.['X-API-Key'],
            }
        });

        // Handle specific error cases
        if (error.response?.status === 401) {
            console.error('Authentication failed - check API key configuration');
        } else if (error.response?.status === 403) {
            console.error('CORS or origin validation failed');
        } else if (error.response?.status === 429) {
            console.error('Rate limit exceeded');
        }

        return Promise.reject(error);
    }
);

export interface UserData {
    userId: string;
    selectedLanguage: string;
    memorizedSentences: Array<{
        en: string;
        hi: string;
        category: string;
        timestamp: Date;
    }>;

    favoriteSentences: Array<{
        en: string;
        hi: string;
        category: string;
        timestamp: Date;
    }>;
    favoriteWords: Array<{
        en: string;
        hi: string;
        example: string;
        timestamp: Date;
    }>;
    completedSentences: Array<{
        en: string;
        hi: string;
        category: string;
        timestamp: Date;
    }>;
    completedWords: Array<{
        en: string;
        hi: string;
        example: string;
        timestamp: Date;
    }>;
    grammarProgress: {
        partsOfSpeech: {
            [key: string]: { completed: string[]; current: string };
        };
        tenses: {
            [key: string]: { completed: string[]; current: string };
        };
    };
}

export const api = {
    // Get user data
    async getUserData(userId: string): Promise<UserData> {
        const response = await apiClient.get(`/user/${userId}`);
        return response.data;
    },

    // Update user language
    async updateUserLanguage(userId: string, selectedLanguage: string): Promise<void> {
        console.log('Making API call to update language:', `/user/${userId}/language`);
        const response = await apiClient.put(`/user/${userId}/language`, { selectedLanguage });
        console.log('Language updated successfully via API');
    },

    // Add memorized sentence
    async addMemorizedSentence(userId: string, en: string, hi: string, category: string): Promise<void> {
        const response = await apiClient.post(`/user/${userId}/memorized-sentences`, { en, hi, category });
        if (response.status !== 200) throw new Error('Failed to add memorized sentence');
    },



    // Add favorite sentence
    async addFavoriteSentence(userId: string, itemId: string, wordCount: number): Promise<void> {
        const response = await apiClient.post(`/favorite/sentences/${userId}/${itemId}`, { wordCount });
        if (response.status !== 200) {
            let msg = 'Failed to add favorite sentence';
            try {
                const data = response.data;
                if (data && data.error) msg = data.error;
            } catch { }
            throw new Error(msg);
        }
    },

    // Add favorite word
    async addFavoriteWord(userId: string, itemId: string, difficulty: string): Promise<void> {
        const response = await apiClient.post(`/favorite/words/${userId}/${itemId}`, { difficulty });
        if (response.status !== 200) {
            let msg = 'Failed to add favorite word';
            try {
                const data = response.data;
                if (data && data.error) msg = data.error;
            } catch { }
            throw new Error(msg);
        }
    },

    // Add favorite grammar
    async addFavoriteGrammar(userId: string, itemId: string, topic: string): Promise<void> {
        const response = await apiClient.post(`/favorite/grammar/${userId}/${itemId}`, { topic });
        if (response.status !== 200) {
            let msg = 'Failed to add favorite grammar';
            try {
                const data = response.data;
                if (data && data.error) msg = data.error;
            } catch { }
            throw new Error(msg);
        }
    },

    // Add completed sentence
    async addCompletedSentence(userId: string, itemId: string, wordCount: number): Promise<void> {
        const response = await apiClient.post(`/completed/sentences/${userId}/${itemId}`, { wordCount });
        if (response.status !== 200) throw new Error('Failed to add completed sentence');
    },

    // Add completed word
    async addCompletedWord(userId: string, itemId: string, difficulty: string): Promise<void> {
        const response = await apiClient.post(`/completed/words/${userId}/${itemId}`, { difficulty });
        if (response.status !== 200) throw new Error('Failed to add completed word');
    },

    // Add completed grammar
    async addCompletedGrammar(userId: string, itemId: string, topic: string): Promise<void> {
        const response = await apiClient.post(`/completed/grammar/${userId}/${itemId}`, { topic });
        if (response.status !== 200) throw new Error('Failed to add completed grammar');
    },

    // Update grammar progress
    async updateGrammarProgress(userId: string, grammarProgress: UserData['grammarProgress']): Promise<void> {
        const response = await apiClient.put(`/user/${userId}/grammar-progress`, { grammarProgress });
        if (response.status !== 200) {
            const errorText = response.data;
            throw new Error(`Failed to update grammar progress: ${response.status} ${errorText}`);
        }
    },

    // Memorize grammar item
    async memorizeGrammar(userId: string, itemId: string, topic: string, status: string): Promise<any> {
        const response = await apiClient.post(`/memorize/grammar/${userId}/${itemId}`, { topic, status });
        if (response.status !== 200) throw new Error('Failed to memorize grammar item');
        return response.data;
    },

    // Memorize sentence item
    async memorizeSentence(userId: string, itemId: string, wordCount: number, status: string): Promise<any> {
        const response = await apiClient.post(`/memorize/sentences/${userId}/${itemId}`, { wordCount, status });
        if (response.status !== 200) throw new Error('Failed to memorize sentence item');
        return response.data;
    },

    // Memorize word item
    async memorizeWord(userId: string, itemId: string, difficulty: string, status: string): Promise<any> {
        const response = await apiClient.post(`/memorize/words/${userId}/${itemId}`, { difficulty, status });
        if (response.status !== 200) throw new Error('Failed to memorize word item');
        return response.data;
    },

    // Remove favorite grammar
    async removeFavoriteGrammar(userId: string, itemId: string): Promise<void> {
        const response = await apiClient.delete(`/favorite/grammar/${userId}/${itemId}`);
        if (response.status !== 200) throw new Error('Failed to remove favorite grammar');
    },

    // Remove favorite sentence
    async removeFavoriteSentence(userId: string, itemId: string): Promise<void> {
        const response = await apiClient.delete(`/favorite/sentences/${userId}/${itemId}`);
        if (response.status !== 200) throw new Error('Failed to remove favorite sentence');
    },

    // Remove favorite word
    async removeFavoriteWord(userId: string, itemId: string): Promise<void> {
        const response = await apiClient.delete(`/favorite/words/${userId}/${itemId}`);
        if (response.status !== 200) throw new Error('Failed to remove favorite word');
    },

    // Get favorite grammar
    async getFavoriteGrammar(userId: string, topic: string): Promise<any[]> {
        const response = await apiClient.get(`/favorite/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to get favorite grammar');
        return response.data;
    },

    // Get master grammar
    async getMasterGrammar(topic: string, userId?: string, page: number = 1, limit: number = 50): Promise<{ items: any[], totalCount: number, page: number, totalPages: number }> {
        let url = `/master/grammar/${topic}?page=${page}&limit=${limit}`;
        if (userId) url += `&userId=${userId}`;
        const response = await apiClient.get(url);
        if (response.status !== 200) throw new Error('Failed to fetch master grammar');
        return response.data;
    },

    // Get master sentences
    async getMasterSentences(wordCount: number, userId?: string, page: number = 1, limit: number = 50): Promise<{ items: any[], totalCount: number, page: number, totalPages: number }> {
        let url = `/master/sentences/${wordCount}?page=${page}&limit=${limit}`;
        if (userId) url += `&userId=${userId}`;
        const response = await apiClient.get(url);
        if (response.status !== 200) throw new Error('Failed to fetch master sentences');
        return response.data;
    },

    // Get master words
    async getMasterWords(difficulty: string, userId?: string, page: number = 1, limit: number = 50): Promise<{ items: any[], totalCount: number, page: number, totalPages: number }> {
        let url = `/master/words/${difficulty}?page=${page}&limit=${limit}`;
        if (userId) url += `&userId=${userId}`;
        const response = await apiClient.get(url);
        if (response.status !== 200) throw new Error('Failed to fetch master words');
        return response.data;
    },

    // Get memorized grammar
    async getMemorizedGrammar(userId: string, topic: string): Promise<any[]> {
        const response = await apiClient.get(`/memorize/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorized grammar');
        const data = response.data;
        return data.items || data;
    },

    // Get memorized sentences
    async getMemorizedSentences(userId: string, wordCount: number): Promise<any[]> {
        const response = await apiClient.get(`/memorize/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorized sentences');
        const data = response.data;
        return data.items || data;
    },

    // Get memorized words
    async getMemorizedWords(userId: string, difficulty: string): Promise<any[]> {
        const response = await apiClient.get(`/memorize/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorized words');
        const data = response.data;
        return data.items || data;
    },

    // Get favorite sentences
    async getFavoriteSentences(userId: string, wordCount: number): Promise<any[]> {
        const response = await apiClient.get(`/favorite/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to get favorite sentences');
        const data = response.data;
        return data.items || data;
    },

    // Get favorite words
    async getFavoriteWords(userId: string, difficulty: string): Promise<any[]> {
        const response = await apiClient.get(`/favorite/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to get favorite words');
        const data = response.data;
        return data.items || data;
    },

    // Get completed sentences
    async getCompletedSentences(userId: string, wordCount: number): Promise<any[]> {
        const response = await apiClient.get(`/completed/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed sentences');
        const data = response.data;
        return data.items || [];
    },

    // Get completed words
    async getCompletedWords(userId: string, difficulty: string): Promise<any[]> {
        const response = await apiClient.get(`/completed/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed words');
        const data = response.data;
        return data.items || data;
    },

    // Get completed grammar
    async getCompletedGrammar(userId: string, topic: string): Promise<any[]> {
        const response = await apiClient.get(`/completed/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed grammar');
        const data = response.data;
        return data.items || data;
    },

    // --- COUNT API METHODS ---
    // Master collection counts (for main pages)
    async getMasterGrammarCount(topic: string): Promise<number> {
        const response = await apiClient.get(`/count/master/grammar/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch grammar count');
        const data = response.data;
        return data.count;
    },

    async getMasterSentencesCount(wordCount: number): Promise<number> {
        const response = await apiClient.get(`/count/master/sentences/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch sentences count');
        const data = response.data;
        return data.count;
    },

    async getMasterWordsCount(difficulty: string): Promise<number> {
        const response = await apiClient.get(`/count/master/words/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch words count');
        const data = response.data;
        return data.count;
    },

    // User-specific counts (for memorize, favorite, completed pages)
    async getMemorizeGrammarCount(userId: string, topic: string): Promise<number> {
        const response = await apiClient.get(`/count/memorize/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorize grammar count');
        const data = response.data;
        return data.count;
    },

    async getMemorizeSentencesCount(userId: string, wordCount: number): Promise<number> {
        const response = await apiClient.get(`/count/memorize/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorize sentences count');
        const data = response.data;
        return data.count;
    },

    async getMemorizeWordsCount(userId: string, difficulty: string): Promise<number> {
        const response = await apiClient.get(`/count/memorize/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch memorize words count');
        const data = response.data;
        return data.count;
    },

    async getFavoriteGrammarCount(userId: string, topic: string): Promise<number> {
        const response = await apiClient.get(`/count/favorite/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch favorite grammar count');
        const data = response.data;
        return data.count;
    },

    async getFavoriteSentencesCount(userId: string, wordCount: number): Promise<number> {
        const response = await apiClient.get(`/count/favorite/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch favorite sentences count');
        const data = response.data;
        return data.count;
    },

    async getFavoriteWordsCount(userId: string, difficulty: string): Promise<number> {
        const response = await apiClient.get(`/count/favorite/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch favorite words count');
        const data = response.data;
        return data.count;
    },

    async getCompletedGrammarCount(userId: string, topic: string): Promise<number> {
        const response = await apiClient.get(`/count/completed/grammar/${userId}/${topic}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed grammar count');
        const data = response.data;
        return data.count;
    },

    async getCompletedSentencesCount(userId: string, wordCount: number): Promise<number> {
        const response = await apiClient.get(`/count/completed/sentences/${userId}/${wordCount}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed sentences count');
        const data = response.data;
        return data.count;
    },

    async getCompletedWordsCount(userId: string, difficulty: string): Promise<number> {
        const response = await apiClient.get(`/count/completed/words/${userId}/${difficulty}`);
        if (response.status !== 200) throw new Error('Failed to fetch completed words count');
        const data = response.data;
        return data.count;
    },

    // Get sentence progress (total and completed) for a user
    async getSentenceProgress(userId: string): Promise<{ total: number, completed: number }> {
        const response = await apiClient.get(`/progress/sentences/${userId}`);
        if (response.status !== 200) throw new Error('Failed to fetch sentence progress');
        return response.data;
    },

    // Robo-teacher usage tracking
    async getRoboTeacherUsage(userId: string): Promise<{
        dailyLimitMinutes: number;
        todayUsageSeconds: number;
        remainingSeconds: number;
        isActive: boolean;
        canUse: boolean;
    }> {
        const response = await apiClient.get(`/user/${userId}/robo-teacher/usage`);
        if (response.status !== 200) throw new Error('Failed to fetch robo-teacher usage');
        return response.data;
    },

    async startRoboTeacherSession(userId: string): Promise<{
        success: boolean;
        sessionStartTime: string;
        remainingSeconds: number;
    }> {
        const response = await apiClient.post(`/user/${userId}/robo-teacher/start`);
        if (response.status !== 200) throw new Error('Failed to start robo-teacher session');
        return response.data;
    },

    async stopRoboTeacherSession(userId: string): Promise<{
        success: boolean;
        sessionDurationSeconds: number;
        totalUsageSeconds: number;
        dailyLimitMinutes: number;
    }> {
        const response = await apiClient.post(`/user/${userId}/robo-teacher/stop`);
        if (response.status !== 200) throw new Error('Failed to stop robo-teacher session');
        return response.data;
    },

    async updateRoboTeacherLimit(userId: string, dailyLimitMinutes: number): Promise<{
        success: boolean;
        dailyLimitMinutes: number;
    }> {
        const response = await apiClient.put(`/user/${userId}/robo-teacher/limit`, { dailyLimitMinutes });
        if (response.status !== 200) throw new Error('Failed to update robo-teacher limit');
        return response.data;
    },
}; 